import React from 'react';

import BusinessIcon from '@mui/icons-material/Business';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloseIcon from '@mui/icons-material/Close';
import DescriptionIcon from '@mui/icons-material/Description';
import GroupIcon from '@mui/icons-material/Group';
import WorkIcon from '@mui/icons-material/Work';
import { Box, Button, Divider, IconButton } from '@sunwisesoftware/sunwise-ui';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import { IconPanel } from 'common/components/icons';

import useCompanyOptions from './hooks/useCompanyOptions';
import useProductOptions from './hooks/useProductOptions';
import Logo from './Logo';
import MenuOption from './MenuOption';
import MenuWithOptions from './MenuWithOptions';
import StyledList from './StyledComponents/StyledList';

const DrawerContent = ({
    container,
    drawerSize,
    handleDrawerToggle,
    isOpenDrawer,
    match,
    mobileOpen,
    setMobileOpen,
    setOpenDrawer,
    setSidebarOpen,
    sidebarOpen,
    toggleDrawerSize,
}) => {
    const { t } = useTranslation();
    const companyOptions = useCompanyOptions();
    const productOptions = useProductOptions();

    const commonMenuProps = {
        container,
        drawerSize,
        handleDrawerToggle,
        isOpenDrawer,
        mobileOpen,
        setMobileOpen,
        setOpenDrawer,
        setSidebarOpen,
        sidebarOpen,
    };
    return (
        <>
            <Box
                display="flex"
                sx={{
                    alignItems: 'center',
                    justifyContent: { md: 'center', xs: 'space-between' },
                    px: { md: 0, xs: 2 },
                }}
            >
                <Box sx={{ flex: { md: 1, xs: 0 }, pb: 1 }}>
                    <Box sx={{ height: '30px', mx: '10px', width: '30px' }}>
                        <Logo />
                    </Box>
                </Box>

                <IconButton
                    onClick={() => {
                        if (mobileOpen) handleDrawerToggle();
                    }}
                    sx={{ display: { md: 'none' } }}
                >
                    <CloseIcon />
                </IconButton>
            </Box>

            <Divider sx={{ display: { md: 'none', sx: 'block' } }} />

            <Button
                sx={{
                    alignSelf: 'end',
                    background: (theme) => theme.palette.background.paper,
                    border: '0px',
                    borderRadius: 1,
                    boxShadow: 'none',
                    color: '#000000',
                    display: {
                        md: 'flex',
                        xs: 'none',
                    },
                    height: '28px',
                    margin: 1,
                    mb: 1,
                    minWidth: '28px',
                    padding: 0,
                    position: 'relative',
                    right: '-12px',
                    width: '28px',
                }}
                onClick={toggleDrawerSize}
            >
                {drawerSize === 'normal' && !sidebarOpen ? (
                    <ChevronLeftIcon sx={{ fontSize: '20px' }} />
                ) : (
                    <ChevronRightIcon sx={{ fontSize: '20px' }} />
                )}
            </Button>

            <StyledList
                sx={{
                    gap: 0,
                    mb: 2,
                }}
            >
                <MenuOption
                    {...{
                        ...commonMenuProps,
                        Icon: GroupIcon,
                        selected:
                            /^\/contacts/i.test(match.path) ||
                            /^\/panel-layout/i.test(match.path),
                        title: t('Contact', { count: 2 }),
                        to: '/contacts',
                    }}
                />

                <MenuOption
                    {...{
                        ...commonMenuProps,
                        Icon: WorkIcon,
                        selected:
                            match.path === '/' || match.path === '/projects',
                        title: t('Project', { count: 2 }),
                        to: '/projects',
                    }}
                />

                <MenuOption
                    {...{
                        ...commonMenuProps,
                        Icon: DescriptionIcon,
                        selected: /^\/racking-proposals/i.test(match.path),
                        title: t('Proposal', { count: 2 }),
                        to: '/racking-proposals',
                    }}
                />
            </StyledList>

            <Divider />

            <StyledList
                sx={{
                    gap: 0,
                    mt: 1,
                }}
            >
                <MenuWithOptions
                    {...commonMenuProps}
                    Icon={IconPanel}
                    iconId="products-icon"
                    options={productOptions}
                    title={t('Product', { count: 2 })}
                />

                <MenuWithOptions
                    {...commonMenuProps}
                    Icon={BusinessIcon}
                    iconId="business-icon"
                    options={companyOptions}
                    title={t('Business')}
                />

                <Divider
                    sx={{
                        marginTop: 2,
                    }}
                />
            </StyledList>
        </>
    );
};

DrawerContent.propTypes = {
    container: PropTypes.func,
    drawerSize: PropTypes.string,
    handleDrawerToggle: PropTypes.func,
    isOpenDrawer: PropTypes.bool,
    match: PropTypes.object,
    mobileOpen: PropTypes.bool,
    setMobileOpen: PropTypes.func,
    setOpenDrawer: PropTypes.func,
    setSidebarOpen: PropTypes.func,
    sidebarOpen: PropTypes.bool,
    toggleDrawerSize: PropTypes.func,
};

export default DrawerContent;
